import React, { useState } from 'react';
import {
  Circle,
  Loader,
  Ban,
  Settings,
  ChevronRight,
  Save,
  X,
} from 'lucide-react';
import { getInitials, getBranchMeta } from '../utils/format';
import styles from './SettingsPanel.module.css';

const MODES = [
  { id: 'normal', name: 'Normal', icon: Circle, description: 'Standard operating rhythm' },
  { id: 'sprint', name: 'Sprint', icon: Loader, description: 'Focused delivery push' },
  { id: 'crisis', name: 'Crisis', icon: Ban, description: 'All-hands response mode' },
];

function ModeSelector({ currentMode }) {
  const [selected, setSelected] = useState(currentMode || 'normal');
  const [confirming, setConfirming] = useState(false);

  const confirmMode = (mode) => {
    setSelected(mode);
    setConfirming(mode);
  };

  const applyMode = () => {
    // In a full implementation this would PATCH /api/settings/mode
    setConfirming(false);
  };

  return (
    <div className={styles.modeSection}>
      <h2 className={styles.sectionTitle}>Company Mode</h2>
      <div className={styles.modeCards} role="radiogroup" aria-label="Company mode">
        {MODES.map((mode) => {
          const Icon = mode.icon;
          const isSelected = selected === mode.id;
          const isCurrent = (currentMode || 'normal') === mode.id;
          return (
            <button
              key={mode.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={`${styles.modeCard} ${isSelected ? styles.modeCardSelected : ''}`}
              onClick={() => confirmMode(mode.id)}
            >
              <Icon size={24} aria-hidden="true" />
              <span className={styles.modeCardName}>{mode.name}</span>
              <span className={styles.modeCardDescription}>{mode.description}</span>
              {isCurrent && <span className={styles.modeCurrent}>Current</span>}
            </button>
          );
        })}
      </div>

      {confirming && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal} role="alertdialog" aria-describedby="mode-confirm-body">
            <h3 className={styles.modalTitle}>Change Company Mode?</h3>
            <p id="mode-confirm-body" className={styles.modalBody}>
              This will affect all agents. Current mode:
              {' '}
              <strong>{currentMode || 'normal'}</strong>
              {' → '}
              <strong>{confirming}</strong>.
            </p>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={applyMode}
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigEditor({ roleKey, role, onCancel }) {
  const [draft, setDraft] = useState(JSON.stringify(role, null, 2));
  const [dirty] = useState(false);

  return (
    <div className={styles.editor}>
      <label className={styles.editorLabel} htmlFor={`config-${roleKey}`}>
        Configuration JSON
      </label>
      <textarea
        id={`config-${roleKey}`}
        className={styles.editorTextarea}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={8}
      />
      <div className={styles.editorActions}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel}>
          <X size={14} /> Cancel
        </button>
        <button type="button" className={styles.btnPrimary}>
          <Save size={14} /> Save
        </button>
        {dirty && <span className={styles.dirtyIndicator} aria-label="Unsaved changes" />}
      </div>
    </div>
  );
}

function RoleConfigList({ roles }) {
  const [editing, setEditing] = useState(null);

  if (!roles) return null;

  return (
    <div className={styles.configSection}>
      <h2 className={styles.sectionTitle}>Role Configuration</h2>
      <ul className={styles.configList}>
        {Object.entries(roles).map(([roleKey, role]) => {
          const branchMeta = getBranchMeta(role.branch);
          return (
            <li key={roleKey} className={styles.configItem}>
              <div className={styles.configItemHeader}>
                <span
                  className={styles.configAvatar}
                  style={{
                    backgroundColor: `${branchMeta.color}20`,
                    color: branchMeta.color,
                  }}
                >
                  {getInitials(role.title)}
                </span>
                <div className={styles.configIdentity}>
                  <span className={styles.configName}>{role.title}</span>
                  <span
                    className={styles.branchBadge}
                    style={{
                      backgroundColor: `${branchMeta.color}12`,
                      color: branchMeta.color,
                    }}
                  >
                    {role.branch}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.editButton}
                  onClick={() => setEditing(editing === roleKey ? null : roleKey)}
                  aria-expanded={editing === roleKey}
                >
                  Edit
                  <ChevronRight
                    size={14}
                    className={`${styles.editIcon} ${editing === roleKey ? styles.editIconOpen : ''}`}
                  />
                </button>
              </div>
              {editing === roleKey && (
                <ConfigEditor roleKey={roleKey} role={role} onCancel={() => setEditing(null)} />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function SettingsPanel({ config, status }) {
  return (
    <section className={styles.panel} aria-label="Settings">
      <div className={styles.header}>
        <Settings size={20} aria-hidden="true" />
        <h2 className={styles.title}>Settings</h2>
      </div>
      <ModeSelector currentMode={status?.company_mode || config?.company_mode} />
      <RoleConfigList roles={config?.roles} />
    </section>
  );
}
